import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ZodError } from 'zod';
import { applyPublicationEdit, publicationEditOf, type PublicationEditInput } from '../../shared/publicationEditing';
import { ApiRequestError } from '../auth/authApi';
import { getEditablePublication, updatePublication, type EditablePublication } from './publicationApi';

export function PublicationEditor({ initial, onCancel, onSaved }: {
  initial: EditablePublication;
  onCancel: () => void;
  onSaved: (saved: EditablePublication) => void;
}) {
  const [source, setSource] = useState(initial);
  const [input, setInput] = useState(() => publicationEditOf(initial.game));
  const [reviewing, setReviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [conflict, setConflict] = useState(false);
  const reviewRef = useRef<HTMLDivElement>(null);
  const baseline = publicationEditOf(source.game);
  const dirty = JSON.stringify(input) !== JSON.stringify(baseline);
  const changedQuestions = input.questions.flatMap((q, i) => JSON.stringify(q) !== JSON.stringify(baseline.questions[i]) ? [i] : []);

  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
  useEffect(() => { if (reviewing) reviewRef.current?.focus(); }, [reviewing]);

  function editQuestion(index: number, update: (question: PublicationEditInput['questions'][number]) => PublicationEditInput['questions'][number]) {
    setInput(current => ({ ...current, questions: current.questions.map((q, i) => i === index ? update(q) : q) }));
    setReviewing(false); setMessage('');
  }
  function inspect(event: FormEvent) {
    event.preventDefault();
    try {
      const next = applyPublicationEdit(source.game, input);
      setInput(publicationEditOf(next)); setMessage(''); setReviewing(true);
    } catch (error) {
      setMessage(error instanceof ZodError ? error.issues.map(issue => {
        const index = issue.path[0] === 'questions' && typeof issue.path[1] === 'number' ? `${issue.path[1] + 1}. soru: ` : '';
        return index + issue.message;
      }).join(' ') : error instanceof Error ? error.message : 'Değişiklikler kontrol edilemedi.');
    }
  }
  async function save() {
    setSaving(true); setMessage('Yayın ve geçmiş sonuçlar güncelleniyor…');
    try { onSaved(await updatePublication(source.publication.id, input)); }
    catch (error) {
      setConflict(error instanceof ApiRequestError && error.status === 409);
      setMessage(error instanceof ApiRequestError ? error.message : 'Güncelleme tamamlanamadı. Formdaki değişikliklerin korunuyor.');
    } finally { setSaving(false); }
  }
  async function reload() {
    if (dirty && !window.confirm('Formdaki değişikliklerin silinerek güncel yayın yüklensin mi?')) return;
    setSaving(true);
    try {
      const fresh = await getEditablePublication(source.publication.id);
      setSource(fresh); setInput(publicationEditOf(fresh.game)); setReviewing(false); setConflict(false); setMessage('Güncel yayın yüklendi.');
    } catch (error) { setMessage(error instanceof ApiRequestError ? error.message : 'Yayın yüklenemedi.'); }
    finally { setSaving(false); }
  }
  return <section className="editor-shell publication-editor" aria-labelledby="publication-editor-title">
    <div className="editor-heading">
      <div><p className="eyebrow">Aktif yayın · Sürüm {source.game.revision ?? 1}</p>
        <h2 id="publication-editor-title">Yayını düzenle</h2>
        <p>Bağlantı aynı kalır. {new Date(source.publication.expiresAt).toLocaleString('tr-TR')} tarihine kadar açık.</p>
      </div>
      <button className="ghost-button" type="button" disabled={saving} onClick={() => {
        if (!dirty || window.confirm('Kaydedilmemiş değişikliklerden vazgeçilsin mi?')) onCancel();
      }}>Panele dön</button>
    </div>
    <p className="publication-edit-notice">Soru ve cevap metinlerini, doğru seçeneği ve başlığı düzeltebilirsin.
      Görseller, soru sırası ve oyun ayarları korunur. Taslağın değişmez.</p>
    <form className="editor-form" onSubmit={inspect}>
      <fieldset className="publication-edit-fields" disabled={saving}>
        <label>Yayın başlığı<input autoFocus value={input.title} minLength={3} maxLength={100} required onChange={e => {
          setInput(current => ({ ...current, title: e.target.value })); setReviewing(false); setMessage('');
        }} /></label>
        {input.questions.map((question, i) => <fieldset className="question-card" key={i}>
          <legend>{i + 1}. soru</legend>
          {source.game.questions[i].media && <img className="publication-edit-media" src={source.game.questions[i].media!.url} alt={`${i + 1}. sorunun mevcut görseli`} />}
          <label>Soru metni<textarea value={question.prompt} maxLength={240} rows={3} onChange={e => editQuestion(i, q => ({ ...q, prompt: e.target.value }))} /></label>
          <div className="publication-edit-choices">
            {question.choices.map((choice, j) => <div className={`publication-edit-choice${choice.isCorrect ? ' is-correct' : ''}`} key={j}>
              <label className="publication-edit-correct"><input type="radio" name={`publication-correct-${i}`} checked={choice.isCorrect}
                aria-label={`${i + 1}. sorunun ${j + 1}. seçeneğini doğru olarak işaretle`}
                onChange={() => editQuestion(i, q => ({ ...q, choices: q.choices.map((c, k) => ({ ...c, isCorrect: k === j })) }))} />Doğru cevap</label>
              <label>{j + 1}. seçenek<input value={choice.text} maxLength={100} onChange={e => editQuestion(i, q => ({
                ...q, choices: q.choices.map((c, k) => k === j ? { ...c, text: e.target.value } : c),
              }))} /></label>
              {source.game.questions[i].choices[j].media && <img className="publication-edit-media" src={source.game.questions[i].choices[j].media!.url} alt={`${j + 1}. seçeneğin mevcut görseli`} />}
            </div>)}
          </div>
        </fieldset>)}
      </fieldset>
      {reviewing && <div className="publication-edit-review" ref={reviewRef} tabIndex={-1} role="region" aria-label="Değişiklik özeti">
        <h3>Değişiklik özeti</h3>
        {input.title !== baseline.title && <p>Başlık: {baseline.title} → <strong>{input.title}</strong></p>}
        <ul>{changedQuestions.map(i => {
          const before = baseline.questions[i].choices.findIndex(c => c.isCorrect);
          const after = input.questions[i].choices.findIndex(c => c.isCorrect);
          return <li key={i}><strong>{i + 1}. soru</strong>: {input.questions[i].prompt || 'Görsel soru'}
            {before !== after && <p>Doğru cevap: {before + 1}. seçenek ({baseline.questions[i].choices[before].text || 'Görsel seçenek'}) → {after + 1}. seçenek ({input.questions[i].choices[after].text || 'Görsel seçenek'})</p>}
            {input.questions[i].prompt !== baseline.questions[i].prompt && <p>Soru metni: {baseline.questions[i].prompt || 'Görsel soru'} → {input.questions[i].prompt || 'Görsel soru'}</p>}
            {input.questions[i].choices.map((c, j) => c.text !== baseline.questions[i].choices[j].text
              ? <p key={j}>{j + 1}. seçenek: {baseline.questions[i].choices[j].text || 'Görsel seçenek'} → {c.text || 'Görsel seçenek'}</p> : null)}
          </li>;
        })}</ul>
        <p>Geçmiş raporlarda doğru cevap güncellenir. Cansız modların puanları, seri ve bonusları kayıtlı seçimlerle yeniden hesaplanır.
          Canlı modların (3 veya 4 can) puanları korunur. Eski kayıtta seçim bilgisi yoksa puan korunur ve raporda belirtilir.
          Başlamış oyunlar kendi sürümüyle tamamlanır; sonucu da aynı düzeltme kuralıyla değerlendirilir.</p>
      </div>}
      <div className="editor-actions">
        {message && <p className="editor-message" role="status">{message}</p>}
        {conflict && <button className="ghost-button" type="button" disabled={saving} onClick={() => void reload()}>Güncel yayını yükle</button>}
        {reviewing ? <>
          <button className="ghost-button" type="button" disabled={saving} onClick={() => setReviewing(false)}>Düzenlemeye dön</button>
          <button className="primary-button" type="button" disabled={saving || !dirty || conflict} onClick={() => void save()}>{saving ? 'Güncelleniyor…' : 'Değişiklikleri yayına uygula'}</button>
        </> : <button className="primary-button" type="submit" disabled={saving || !dirty}>Değişiklikleri incele</button>}
      </div>
    </form>
  </section>;
}
